import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
  SecurityContext,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import { provideMarkdown } from 'ngx-markdown';

import { routes } from './app.routes';
import { environment } from '../environments/environment.development';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideHeyApiClient } from './api/client/client.gen';
import { client } from './api/client.gen';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ConsoleErrorHandler, reportFailedRequests } from './error-reporting';

const localhostCondition =
  createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: new RegExp(
      `^(${(getEnv() || environment).api.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      )})(.*)?$`,
      'i',
    ),
    bearerPrefix: 'Bearer',
  });

export function getEnv(): typeof environment {
  return (window as any)['env'] || environment;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHeyApiClient(client),
    provideKeycloak({
      config: {
        url: getEnv().keycloak.url,
        realm: getEnv().keycloak.realm,
        clientId: getEnv().keycloak.clientId,
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
          sessionTimeout: 60000,
        }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [localhostCondition],
    },
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, reportFailedRequests]),
      withFetch(),
    ),
    { provide: ErrorHandler, useClass: ConsoleErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideMarkdown(),
    importProvidersFrom(FlexLayoutModule),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    // Security configuration
    {
      provide: SecurityContext,
      useValue: SecurityContext.HTML,
    },
  ],
};
